// Preparo da logo do parceiro para uso no app.
//
// O upload de foto de cavalo (pages/horses/HorseList.tsx) manda o arquivo CRU
// em base64 dentro do JSON: aceita até 5 MB, não olha dimensão e não reduz
// nada. Para a logo isso não serve — ela é baixada por todo usuário, a cada
// abertura da home do app, e precisa ficar nítida numa faixa de 32 px de
// altura (96 px em tela retina).
//
// Aqui o arquivo é validado e REAMOSTRADO para um tamanho previsível antes de
// virar base64.

/** Altura final, em pixels. 32 lógicos × 3 (retina) = 96. */
const ALTURA_ALVO = 96;
/** Teto de largura, para uma logo muito horizontal não ocupar a barra toda. */
const LARGURA_MAXIMA = 480;
/** Mínimos aceitos no arquivo de origem — abaixo disso a logo sai borrada. */
const LARGURA_MINIMA_ORIGEM = 200;
const ALTURA_MINIMA_ORIGEM = 60;
/** Teto do arquivo enviado. */
const BYTES_MAXIMOS = 2 * 1024 * 1024;

const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/webp'];

export interface LogoPreparada {
  /** Base64 SEM o prefixo `data:` — mesmo formato da foto do cavalo. */
  base64: string;
  /** Data URI pronta para <img src>, para a pré-visualização. */
  dataUri: string;
  largura: number;
  altura: number;
  bytes: number;
}

export class LogoInvalidaError extends Error {
  readonly motivo:
    | 'tipo'
    | 'peso'
    | 'dimensao'
    | 'leitura';
  constructor(motivo: LogoInvalidaError['motivo'], mensagem: string) {
    super(mensagem);
    this.motivo = motivo;
    this.name = 'LogoInvalidaError';
  }
}

const carregarImagem = (arquivo: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new LogoInvalidaError('leitura', 'Não foi possível ler a imagem.'));
    };
    img.src = url;
  });

/**
 * Valida e reamostra a logo.
 *
 * Sempre devolve PNG: a logo costuma ter fundo transparente, e JPG perderia a
 * transparência, deixando um retângulo branco no tema escuro do app.
 */
export async function prepararLogo(arquivo: File): Promise<LogoPreparada> {
  if (!TIPOS_ACEITOS.includes(arquivo.type)) {
    throw new LogoInvalidaError(
      'tipo',
      `Formato não aceito (${arquivo.type || 'desconhecido'}). Use PNG, JPG ou WebP.`,
    );
  }
  if (arquivo.size > BYTES_MAXIMOS) {
    throw new LogoInvalidaError(
      'peso',
      `Arquivo de ${(arquivo.size / 1024 / 1024).toFixed(1)} MB — o limite é 2 MB.`,
    );
  }

  const img = await carregarImagem(arquivo);

  if (
    img.naturalWidth < LARGURA_MINIMA_ORIGEM ||
    img.naturalHeight < ALTURA_MINIMA_ORIGEM
  ) {
    throw new LogoInvalidaError(
      'dimensao',
      `Imagem de ${img.naturalWidth}×${img.naturalHeight} px — o mínimo é ` +
        `${LARGURA_MINIMA_ORIGEM}×${ALTURA_MINIMA_ORIGEM} px.`,
    );
  }

  // Reamostra mantendo proporção: fixa a altura e respeita o teto de largura.
  let altura = ALTURA_ALVO;
  let largura = Math.round((img.naturalWidth / img.naturalHeight) * altura);
  if (largura > LARGURA_MAXIMA) {
    largura = LARGURA_MAXIMA;
    altura = Math.round((img.naturalHeight / img.naturalWidth) * largura);
  }

  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new LogoInvalidaError('leitura', 'Canvas indisponível no navegador.');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, largura, altura);

  const dataUri = canvas.toDataURL('image/png');
  const base64 = dataUri.split(',')[1] ?? '';

  return {
    base64,
    dataUri,
    largura,
    altura,
    // base64 cresce ~4/3 sobre os bytes reais.
    bytes: Math.round((base64.length * 3) / 4),
  };
}

/** Monta a data URI a partir do que o backend devolver. */
export const logoParaDataUri = (valor?: string | null): string | null => {
  if (!valor) return null;
  const limpo = valor.trim();
  if (!limpo) return null;
  return limpo.startsWith('data:') ? limpo : `data:image/png;base64,${limpo}`;
};

export const LOGO_REQUISITOS = {
  alturaAlvo: ALTURA_ALVO,
  larguraMaxima: LARGURA_MAXIMA,
  larguraMinimaOrigem: LARGURA_MINIMA_ORIGEM,
  alturaMinimaOrigem: ALTURA_MINIMA_ORIGEM,
  bytesMaximos: BYTES_MAXIMOS,
  tiposAceitos: TIPOS_ACEITOS,
};
